import { cn } from "@/lib/utils.ts";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import * as React from "react";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-black/50 backdrop-blur-xs data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    ref={ref}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  {
    hideClose?: boolean;
    /** Restyle the built-in overlay (e.g. a light scrim) without losing its animation or modal semantics. */
    overlayClassName?: string;
    /** Portal target, for a caller that needs the dialog inside a specific element rather than `document.body`. */
    container?: HTMLElement | null;
  } & React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ children, className, hideClose, overlayClassName, container, ...props }, ref) => (
  <DialogPortal container={container ?? undefined}>
    <DialogOverlay className={overlayClassName} />
    <DialogPrimitive.Content
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-[min(32rem,calc(100vw-2.5rem))] translate-x-[-50%] translate-y-[-50%] gap-4 border bg-popover text-popover-foreground p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-bottom-2 data-[state=closed]:slide-out-to-bottom-2 rounded-xl",
        className,
      )}
      ref={ref}
      {...props}
    >
      {children}
      {!hideClose && (
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary data-[state=open]:text-muted-foreground cursor-pointer">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

/**
 * The bounded card: centred in `container` and capped, with a gutter around it.
 *
 * The width cap is the lower of the card's own maximum (42rem, the old
 * `max-w-2xl`) and the container minus a `--panel-gutter` on each side — so a
 * narrowed canvas pane squeezes the card, never the gutter. A bare `max-w-2xl`
 * let `w-full` run the card edge to edge once the pane dropped under 42rem.
 */
const CONTAINED_CARD =
  "absolute left-[50%] top-[50%] max-h-[calc(100%-2rem)] w-full max-w-[min(42rem,calc(100%-var(--panel-gutter)*2))] translate-x-[-50%] translate-y-[-50%] border shadow-lg rounded-xl";

/**
 * The bounded card's entrance: it rises a little as it fades in, the way a small
 * object settling onto a surface does. Separate from the card itself so it can
 * be withheld when the card replaces one already on screen.
 */
const CONTAINED_CARD_ENTRANCE = "data-[state=open]:slide-in-from-bottom-2";

/**
 * The `fullscreen` card: fills `container` edge to edge. The translates have to be
 * zeroed explicitly — `inset-0` beats `left-`/`top-` through tailwind-merge, but
 * transforms are a separate group that no inset utility displaces, so a surviving
 * `translate-x-[-50%]` would shift the filled card half its width off-column.
 *
 * Border, radius, and shadow all go: they describe an object floating over a
 * surface, and there is no surface left to float over. The entrance slide goes
 * for the same reason — the same 0.5rem that reads as a card settling reads as
 * the whole column lurching once it spans the column, so this fades only.
 *
 * Reachable on mount, not just on toggle: the canvas persists its display mode,
 * so a reload can open a card straight into `full`.
 */
const FULLSCREEN_CARD = "absolute inset-0 max-h-none max-w-none translate-x-0 translate-y-0";

/**
 * A dialog scoped to `container` instead of the viewport: an absolutely-positioned
 * card over a scrim that dims only that element, leaving the rest of the app live.
 * Pair with `<Dialog modal={false}>` — a modal dialog's focus trap and scroll lock
 * would fight the surface you deliberately kept interactable.
 *
 * Renders no corner close (unlike {@link DialogContent}); supply your own dismiss
 * in `children`. The scrim closes on click, and Escape closes; interactions
 * outside `container` (e.g. a side panel) are ignored so they don't dismiss it.
 *
 * `fullscreen` swaps the bounded card for one that fills `container`, covering
 * exactly the region the scrim dims. Which region that is follows from what the
 * caller makes `container` — anything it leaves outside stays live.
 */
const DialogContentContained = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  {
    /** Portal target the card + scrim are positioned within. May be null before it mounts. */
    container: HTMLElement | null;
    /** Fill `container` edge-to-edge instead of sitting as a centred, capped card. */
    fullscreen?: boolean;
    /** Recolors the scrim, e.g. a light shim instead of the default dark dim. */
    scrimClassName?: string;
    /**
     * Fade the scrim and card in. Pass `false` when this card is replacing one
     * that is already on screen over the same scrim: the entrance starts at
     * opacity 0, so re-running it there drops the backdrop to nothing and
     * rebuilds it, reading as a black flash rather than a transition.
     */
    animateEntrance?: boolean;
  } & React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(
  (
    {
      children,
      className,
      container,
      fullscreen = false,
      animateEntrance = true,
      scrimClassName,
      ...props
    },
    ref,
  ) => (
    <DialogPortal container={container ?? undefined}>
      {/* Radix renders no Overlay in non-modal mode (it is a modal-only
        affordance), so this is ours. With default pointer-events it both dims and
        blocks clicks to the content behind it, scoped to `container`. Wrapped in
        Close rather than given a handler so a backdrop click dismisses without
        adding a focusable tab stop.

        A plain div, not a Radix primitive, so it never receives `data-state` —
        the entrance is a bare `animate-in`, where a `data-[state=open]:` variant
        would never fire and the scrim would pop in without its fade. */}
      <DialogClose asChild>
        <div
          role="presentation"
          className={cn(
            "absolute inset-0 z-50 bg-black/50 backdrop-blur-xs duration-200",
            animateEntrance && "animate-in fade-in-0",
            scrimClassName,
          )}
        />
      </DialogClose>
      <DialogPrimitive.Content
        onInteractOutside={(e) => e.preventDefault()}
        className={cn(
          "@container z-50 flex w-full flex-col overflow-hidden bg-popover text-popover-foreground duration-200",
          animateEntrance && "data-[state=open]:animate-in data-[state=open]:fade-in-0",
          fullscreen ? FULLSCREEN_CARD : CONTAINED_CARD,
          animateEntrance && !fullscreen && CONTAINED_CARD_ENTRANCE,
          className,
        )}
        ref={ref}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
);
DialogContentContained.displayName = "DialogContentContained";

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    ref={ref}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    className={cn("text-sm text-muted-foreground", className)}
    ref={ref}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogContentContained,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
